import { keccak256, toBytes, type Abi } from 'viem'
import { kycRegistryAbi } from '@/abi/kycRegistryAbi'
import { operatorAbi } from '@/abi/operatorAbi'
import { operatorRolesAbi } from '@/abi/operatorRolesAbi'
import { potSeederAbi } from '@/abi/potSeederAbi'
import { playerRegistryAbi } from '@/abi/playerRegistryAbi'
import type { ContractName } from './contracts'

// OpenZeppelin AccessControl's default admin role is the zero bytes32.
export const DEFAULT_ADMIN_ROLE: `0x${string}` =
    '0x0000000000000000000000000000000000000000000000000000000000000000'

// Role identifier helpers — keccak256 of the role name as bytes.
export function roleId(name: string): `0x${string}` {
    return keccak256(toBytes(name))
}

// Console-RBAC role ids from the on-chain OperatorRoles contract. `admin` is the
// OZ DEFAULT_ADMIN_ROLE; `mlro` / `read_only` are keccak256 of the role names.
// These back both the eligibility/membership reads and the grant/revoke writes.
export const MLRO_ROLE: `0x${string}` = roleId('MLRO_ROLE')
export const READ_ONLY_ROLE: `0x${string}` = roleId('READ_ONLY_ROLE')

export interface OnchainRoleSpec {
    // Human label shown in the dialog.
    label: string
    // bytes32 role identifier.
    bytes32: `0x${string}`
}

export interface OnchainContractSpec {
    name: ContractName
    label: string
    abi: Abi
    roles: OnchainRoleSpec[]
}

// Fixed list of role-bearing contracts the console knows about. Adding a new
// contract here makes its roles appear automatically in the session-roles
// dialog; just make sure the ABI is vendored and addresses are configured.
export const ONCHAIN_CONTRACTS: OnchainContractSpec[] = [
    {
        name: 'playerRegistry',
        label: 'PlayerRegistry',
        abi: playerRegistryAbi as Abi,
        roles: [
            { label: 'DEFAULT_ADMIN_ROLE', bytes32: DEFAULT_ADMIN_ROLE },
            { label: 'OPERATOR_ROLE', bytes32: roleId('OPERATOR_ROLE') },
            { label: 'UPGRADER_ROLE', bytes32: roleId('UPGRADER_ROLE') },
        ],
    },
    {
        name: 'kycRegistry',
        label: 'KycRegistry',
        abi: kycRegistryAbi as Abi,
        roles: [
            { label: 'DEFAULT_ADMIN_ROLE', bytes32: DEFAULT_ADMIN_ROLE },
            { label: 'KYC_ADMIN_ROLE', bytes32: roleId('KYC_ADMIN_ROLE') },
            { label: 'WRITER_ROLE', bytes32: roleId('WRITER_ROLE') },
            { label: 'POLICY_ADMIN_ROLE', bytes32: roleId('POLICY_ADMIN_ROLE') },
            { label: 'UPGRADER_ROLE', bytes32: roleId('UPGRADER_ROLE') },
        ],
    },
    {
        name: 'operator',
        label: 'Operator',
        abi: operatorAbi as Abi,
        roles: [
            { label: 'DEFAULT_ADMIN_ROLE', bytes32: DEFAULT_ADMIN_ROLE },
            { label: 'OPERATOR_ROLE', bytes32: roleId('OPERATOR_ROLE') },
            { label: 'TREASURY_ROLE', bytes32: roleId('TREASURY_ROLE') },
            { label: 'UPGRADER_ROLE', bytes32: roleId('UPGRADER_ROLE') },
        ],
    },
    {
        name: 'potSeeder',
        label: 'PotSeeder',
        abi: potSeederAbi as Abi,
        roles: [
            { label: 'DEFAULT_ADMIN_ROLE', bytes32: DEFAULT_ADMIN_ROLE },
            { label: 'SEEDER_ROLE', bytes32: roleId('SEEDER_ROLE') },
        ],
    },
    {
        name: 'operatorRoles',
        label: 'OperatorRoles',
        abi: operatorRolesAbi as Abi,
        roles: [
            { label: 'DEFAULT_ADMIN_ROLE (admin)', bytes32: DEFAULT_ADMIN_ROLE },
            { label: 'MLRO_ROLE', bytes32: MLRO_ROLE },
            { label: 'READ_ONLY_ROLE', bytes32: READ_ONLY_ROLE },
        ],
    },
]
